import { useCostPriceStore } from '../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../stores/appSubrecipesStore';
import { useOrderHistory } from '../../stores/appOrderHistoryStore';
import { useSourceRestaurantDataStore } from '../../stores/appSourceRestaurantDataStore';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { useWasteNeedsStore } from '../../stores/appWasteNeedsStore';
import { ALL_RESTAURANTS_ID, useAppUserDefaultStore } from '../../stores/appUserDefaultStore';
import type { AdminCustomerRequestDetailDto } from './types';

type CompletedRequest = Pick<AdminCustomerRequestDetailDto, 'type' | 'restaurant' | 'actionResult'>;

/** Refresh only already-loaded account scopes; approval never selects the customer's restaurant. */
export async function refreshAfterCustomerRequestApproval(item: CompletedRequest, options: { keepOrders?: boolean } = {}): Promise<void> {
  if (item.type === 'supplier_order_confirmation' || item.type === 'general_question') return;
  const restaurants = useRestaurantStore.getState();
  const sources = useSourceRestaurantDataStore.getState();
  const needs = useWasteNeedsStore.getState();
  const affected = new Set([item.restaurant.id, ...restaurants.restaurants.map(row => row.id),
    ...Object.keys(sources.dataByRestaurant), ...Object.keys(needs.needs)]);
  for (const target of item.actionResult?.targets ?? []) {
    if (!target.details || typeof target.details !== 'object') continue;
    const ids = (target.details as { affectedRestaurantIds?: unknown }).affectedRestaurantIds;
    if (Array.isArray(ids)) for (const id of ids) if (typeof id === 'string') affected.add(id);
  }
  for (const id of Array.from(affected)) if (id && id !== ALL_RESTAURANTS_ID) needs.invalidate(id);
  sources.clear();

  const selectedScope = useAppUserDefaultStore.getState().restaurant?.id;
  const scopeUnchanged = () => useAppUserDefaultStore.getState().restaurant?.id === selectedScope;
  const cost = useCostPriceStore.getState();
  const prep = useSubrecipesStore.getState();
  const tasks: Array<Promise<unknown>> = [];
  // If navigation already started loading another scope, leave that load in control.
  if (cost.currentRestaurantId && (!selectedScope || selectedScope === cost.currentRestaurantId)) {
    const scope = cost.currentRestaurantId;
    tasks.push(cost.load(scope, { force: true, isCurrent: () => scopeUnchanged() && useCostPriceStore.getState().currentRestaurantId === scope }));
  }
  if (prep.currentRestaurantId && (!selectedScope || selectedScope === prep.currentRestaurantId)) {
    const scope = prep.currentRestaurantId;
    tasks.push(prep.load(scope, { force: true, isCurrent: () => scopeUnchanged() && useSubrecipesStore.getState().currentRestaurantId === scope }));
  }
  if (item.type === 'product_quantity' && !options.keepOrders) {
    const orders = useOrderHistory.getState();
    const scope = orders.currentRestaurantId;
    const platformIds = orders.currentPlatformIds?.slice();
    // This invalidates both raw orders and the product/unit basis derived from them,
    // and cancels the progressive loader through its existing generation counter.
    if (scope && (!selectedScope || selectedScope === scope)) {
      orders.clearOrderhistoryDataForRestaurant(scope);
      if (platformIds) tasks.push(orders.loadByPlatformIds(scope, platformIds, restaurants.platformToSectionIndex));
    }
    // A legacy restaurant-wide load has no captured platform scope or stale-response
    // guard. Leave it invalidated for the normal page loader instead of broadening
    // the current account's data or launching an unguarded background fetch here.
  }
  // An accepted approval remains accepted even if a read-model refresh is offline.
  await Promise.allSettled(tasks);
}
